import { useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { useHistory } from 'react-router-dom';
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonButtons,
  IonMenuButton,
  IonTitle,
  IonContent,
  IonText,
  IonSelect,
  IonSelectOption,
  IonTextarea,
  IonButton,
  IonLoading
} from '@ionic/react';
import { useFormik } from 'formik';
import * as Yup from 'yup';

import { ReactComponent as HeartIcon } from './assets/heart.svg';
import { ReactComponent as OneStar } from './assets/rating-icons/1-star.svg';
import { ReactComponent as TwoStar } from './assets/rating-icons/2-star.svg';
import { ReactComponent as ThreeStar } from './assets/rating-icons/3-star.svg';
import { ReactComponent as FourStar } from './assets/rating-icons/4-star.svg';
import { ReactComponent as FiveStar } from './assets/rating-icons/5-star.svg';
import { ReactComponent as SixStar } from './assets/rating-icons/6-star.svg';

import feedbackStore from 'stores/feedback.store';
import layoutStore from 'stores/layout.store';

import classes from './style.module.scss';

const FeedbackPage = ({ match, history }) => {
  const isLoading = feedbackStore.getIsLoading();
  const { push } = useHistory();

  useEffect(() => {
    return () => {
      feedbackStore.resetAllFields();
    };
  }, []);

  const feedbackForm = useFormik({
    initialValues: {
      topic: null,
      description: null,
      rating: null
    },
    validationSchema: Yup.object({
      topic: Yup.string().nullable(),
      description: Yup.string().nullable(),
      rating: Yup.number().max(6).min(1)
    }),
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: async (values) => {
      try {
        feedbackStore.setFields('id');
        feedbackStore.setDraftObject({
          rating: +values.rating,
          topic: values.topic,
          description: values.description
        });
        const createdFeedback = await feedbackStore.createObject();
        feedbackForm.resetForm();
        layoutStore.setToastConfig({
          message: 'feedback submitted!',
          color: 'success'
        });
        push('/dashboard');
      } catch (error) {
        console.log(error);
        layoutStore.setToastConfig({
          message: 'something went wrong!',
          color: 'danger'
        });
      }
    }
  });

  return (
    <IonPage id='feedback'>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot='start'>
            <IonMenuButton></IonMenuButton>
          </IonButtons>
          <IonTitle>Feedback</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent className={classes.ionContent}>
        <IonText className={classes.title}>
          We'd <HeartIcon className={classes.titleHeartIcon} /> To Hear your
          Feedback
        </IonText>
        <form onSubmit={feedbackForm.handleSubmit}>
          <div className={classes.experienceContainer}>
            <IonText className={classes.caption}>
              Share Overall Experience
            </IonText>
            <div className={classes.rating}>
              <SixStar
                className={
                  feedbackForm.values.rating === 6 ? classes.selected : ''
                }
                onClick={() => feedbackForm.setFieldValue('rating', 6)}
              />
              <FiveStar
                className={
                  feedbackForm.values.rating === 5 ? classes.selected : ''
                }
                onClick={() => feedbackForm.setFieldValue('rating', 5)}
              />
              <FourStar
                className={
                  feedbackForm.values.rating === 4 ? classes.selected : ''
                }
                onClick={() => feedbackForm.setFieldValue('rating', 4)}
              />
              <ThreeStar
                className={
                  feedbackForm.values.rating === 3 ? classes.selected : ''
                }
                onClick={() => feedbackForm.setFieldValue('rating', 3)}
              />
              <TwoStar
                className={
                  feedbackForm.values.rating === 2 ? classes.selected : ''
                }
                onClick={() => feedbackForm.setFieldValue('rating', 2)}
              />
              <OneStar
                className={
                  feedbackForm.values.rating === 1 ? classes.selected : ''
                }
                onClick={() => feedbackForm.setFieldValue('rating', 1)}
              />
            </div>
            {feedbackForm.errors.rating && (
              <IonText color='danger'>
                <p className='ion-padding-start'>
                  {feedbackForm.errors.rating}
                </p>
              </IonText>
            )}
          </div>
          <div className={classes.feedbackForm}>
            <IonSelect
              value={feedbackForm.values.topic}
              placeholder='Select topic'
              onIonChange={(e) =>
                feedbackForm.setFieldValue('topic', e.detail.value)
              }
              interface='action-sheet'
            >
              <IonSelectOption value='General'>General</IonSelectOption>
              <IonSelectOption value='Application'>Application</IonSelectOption>
              <IonSelectOption value='Technical Issue'>
                Technical Issue
              </IonSelectOption>
              <IonSelectOption value='Feature Request'>
                Feature Request
              </IonSelectOption>
            </IonSelect>
            <IonTextarea
              placeholder='Enter more information'
              value={feedbackForm.values.description}
              onIonChange={(e) =>
                feedbackForm.setFieldValue('description', e.detail.value)
              }
              rows={4}
            />
          </div>
          <IonButton fill='outline' type='submit'>
            Submit
          </IonButton>
        </form>
        <IonLoading isOpen={isLoading} />
      </IonContent>
    </IonPage>
  );
};

export default observer(FeedbackPage);
